'use client';

import { useCallback, useMemo } from 'react';
import useMap from '@/hooks/useMap';
import useAnalytics from '@/hooks/useAnalytics';
import { useSearchParams } from 'next/navigation';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import Observer from '@/components/Observer';
import { getAdClickFact } from '@/analytics/helpers/cventAnalytics/getAdClickFact';
import { getCertifiedViewFact } from '@/analytics/helpers/cventAnalytics/getCertifiedViewFact';
import { queryStringConstants } from '@/constants/queryConstants';
import type { AdClickFact } from '@/analytics/models/cventAnalytics/AdClickFact';
import type { CertifiedViewExperimentData } from '@/analytics/models/cventAnalytics/CertifiedViewFact';
import { CSN_FACT_SOURCE_VENUE_CARD } from '@/utils/analyticsClientHelper';
import type { SearchParams } from '@/models/SearchParams';
import { getVenueUrl } from '@/utils/venueDetailsHelper';
import type { Locale } from '@/i18n';
import type { VenueProfileFactParams } from '@/components/card/VenueCard';
import getListingClickFact from '@/analytics/helpers/siteCatalyst/getListingClickFact';
import useSpinner from '@/hooks/useSpinner';

export type Props = {
  venueIndex: number;
  venue: CsnSearchVenue;
  searchTerm: string;
  children: JSX.Element;
  certifiedViewExperimentData: CertifiedViewExperimentData;
  environment: string;
  searchResultsRank?: number;
  adType?: string;
  locale: Locale;
  searchParams: SearchParams;
  cventGuestDomain: string;
  basePath: string;
  isLoggedIn: boolean;
  accountId: string;
};

// Encapsulates the Venue Card within a client component for logging facts.
export function VenueCardContainer({
  venueIndex,
  venue,
  searchTerm,
  certifiedViewExperimentData,
  environment,
  searchResultsRank,
  adType,
  locale,
  searchParams,
  cventGuestDomain,
  basePath,
  children,
  isLoggedIn,
  accountId
}: Props): JSX.Element {
  const { mapExpanded, mapState, setMapState } = useMap();
  const analytics = useAnalytics();
  const params = useSearchParams();
  const { setShowSpinner } = useSpinner();

  const triggerAdClickFact = useCallback(async () => {
    const adClickFact: AdClickFact = getAdClickFact(
      venue,
      venueIndex,
      mapExpanded,
      searchTerm,
      params?.get(queryStringConstants.RFP_ID),
      CSN_FACT_SOURCE_VENUE_CARD
    );
    await analytics.reportFactImmediately<AdClickFact>(adClickFact);
  }, [analytics, mapExpanded, venue, searchTerm, params, venueIndex]);

  const logFactsAndOpenVenueProfile = useCallback(
    async e => {
      setShowSpinner(true);
      e.preventDefault();
      e.stopPropagation();
      // if ad -> trigger ad fact
      if (venue.listingCode) {
        analytics.reportSiteCatalystFact(
          getListingClickFact(isLoggedIn, accountId, venue.name, venue.listingCode, venue.code)
        );
        await triggerAdClickFact();
      }
      const venueProfileFactParams: VenueProfileFactParams = {
        adId: venue.listingCode,
        adType,
        adPos: venue.listingCode ? venueIndex + 1 : undefined,
        searchResultsRank,
        searchTerm
      };
      const venueUrl = getVenueUrl(venue, locale, cventGuestDomain, basePath, searchParams, venueProfileFactParams);

      // open in new tab
      if (e.metaKey) {
        setShowSpinner(false);
        window.open(venueUrl);
      } else {
        window.open(venueUrl, '_self');
      }
    },
    [
      triggerAdClickFact,
      venue,
      locale,
      adType,
      cventGuestDomain,
      basePath,
      searchParams,
      venueIndex,
      searchResultsRank,
      searchTerm,
      analytics,
      accountId,
      isLoggedIn,
      setShowSpinner
    ]
  );

  const onMouseEnter = useCallback(() => {
    setMapState({ ...mapState, hoveredVenue: venue.id });
  }, [mapState, setMapState, venue.id]);

  const onMouseLeave = useCallback(() => {
    setMapState({ ...mapState, hoveredVenue: null });
  }, [mapState, setMapState]);

  const addObserver =
    venue.featuredLevel === 'FOUR_DIAMOND' ||
    venue.featuredLevel === 'DIAMOND_PLUS' ||
    venue.featuredLevel === 'DIAMOND_PLUS_BRAND';
  const observerPayload = useMemo(
    () => getCertifiedViewFact(searchTerm, mapExpanded, venueIndex, venue, environment),
    [environment, mapExpanded, searchTerm, venue, venueIndex]
  );

  return (
    // don't want wrapper div to be accessible to screen reader
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      className={`w-full ${
        mapState.hoveredVenue === venue.id ? 'rounded-xl shadow-4' : 'hover:rounded-xl hover:shadow-4'
      }`}
      onClick={logFactsAndOpenVenueProfile}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {addObserver ? (
        <Observer payload={observerPayload} certifiedViewExperimentData={certifiedViewExperimentData}>
          {children}
        </Observer>
      ) : (
        children
      )}
    </div>
  );
}
